/**
 * Part selector list — renders catalog parts organized by anatomical sections
 * with side duplication for bilateral/directional parts.
 */

import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ExclusivePartGroup } from './exclusive-part-group';
import { PartSelector } from './part-selector';

import { cn } from '@/lib/utils';
import type {
    FieldSelections,
    IAssemblyEntry,
    ICatalogPart,
    IntroductionSide,
    MeasurementLookup,
    PartType,
    Side,
} from '@/workflows/vascular/common/catalog';

// ============================================================================
// Constants
// ============================================================================

/** Part types that should be rendered for both left and right sides. */
const SIDED_PART_TYPES = new Set<string>(['bifurcated_down', 'contra', 'ipsi']);

/** Anatomical sort order for part types. */
const PART_TYPE_ORDER: Record<PartType, number> = {
    bifurcated_up: 0,
    cuff: 1,
    contra: 2,
    ipsi: 3,
    bifurcated_down: 4,
    standard: 5,
    occluder: 6,
};

// ============================================================================
// Types
// ============================================================================

type RenderableItem =
    | { kind: 'part'; part: ICatalogPart }
    | { kind: 'group'; groupName: string; parts: ICatalogPart[] };

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build an ordered list of renderable items from a sorted array of parts.
 * Consecutive parts sharing the same `exclusiveGroup` are collapsed into one group entry.
 */
function buildRenderableItems(sortedParts: ICatalogPart[]): RenderableItem[] {
    const items: RenderableItem[] = [];
    const seenGroups = new Set<string>();

    for (const part of sortedParts) {
        if (part.exclusiveGroup) {
            if (seenGroups.has(part.exclusiveGroup)) continue;
            seenGroups.add(part.exclusiveGroup);
            const groupParts = sortedParts.filter((p) => p.exclusiveGroup === part.exclusiveGroup);
            items.push({ kind: 'group', groupName: part.exclusiveGroup, parts: groupParts });
        } else {
            items.push({ kind: 'part', part });
        }
    }
    return items;
}

/**
 * Resolve the anatomical side for a given part type and L/R section.
 *
 * When introduction is LEFT (default):
 *   - ipsi -> left, contra -> right
 * When introduction is RIGHT:
 *   - ipsi -> right, contra -> left
 */
function resolvePartSide(
    partType: PartType,
    sectionSide: 'left' | 'right',
    introSide: IntroductionSide
): Side {
    if (partType === 'bifurcated_down') return sectionSide;
    if (partType === 'ipsi') return introSide;
    if (partType === 'contra') return introSide === 'left' ? 'right' : 'left';
    return sectionSide;
}

// ============================================================================
// Sub-components
// ============================================================================

function SectionHeader({ label }: { label: string }) {
    return (
        <div className="px-3 py-1 bg-muted/50 border-y border-border/50">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </span>
        </div>
    );
}

function CollapsiblePart({
    label,
    defaultCollapsed,
    children,
}: {
    label: string;
    defaultCollapsed: boolean;
    children: React.ReactNode;
}) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    return (
        <div className="border-b border-border/50">
            <button
                className="flex items-center gap-1 w-full px-3 py-1.5 text-left hover:bg-muted/30 transition-colors"
                onClick={() => setCollapsed((c) => !c)}
            >
                <ChevronRight
                    className={cn(
                        'h-3 w-3 text-muted-foreground transition-transform',
                        !collapsed && 'rotate-90'
                    )}
                />
                <span className="text-xs font-semibold text-foreground">{label}</span>
            </button>
            {!collapsed && children}
        </div>
    );
}

/** Optionally wrap content in a CollapsiblePart if the part supports collapsing. */
function wrapCollapsible(part: ICatalogPart, key: string, content: React.ReactElement): React.ReactElement {
    if (!part.collapsible) return content;
    return (
        <CollapsiblePart key={key} label={part.label} defaultCollapsed={part.collapsedByDefault ?? false}>
            {content}
        </CollapsiblePart>
    );
}

// ============================================================================
// PartSelectorList
// ============================================================================

interface PartSelectorListProps {
    parts: ICatalogPart[];
    measurementLookup: MeasurementLookup;
    onAdd: (partId: string, side: Side, fieldSelections: FieldSelections) => void;
    assembly: readonly IAssemblyEntry[];
    catalogId: string;
    introductionSide: IntroductionSide;
}

export function PartSelectorList({
    parts: rawParts,
    measurementLookup,
    onAdd,
    assembly,
    catalogId,
    introductionSide,
}: PartSelectorListProps) {
    // Filter out hidden parts
    const parts = useMemo(() => rawParts.filter((p) => !p.hidden), [rawParts]);

    const addedUniqueKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const entry of assembly) {
            if (entry.catalogId !== catalogId) continue;
            const part = parts.find((p) => p.id === entry.partId);
            if (part?.unique) keys.add(`${part.id}:${entry.side}`);
        }
        return keys;
    }, [assembly, parts, catalogId]);

    const sorted = useMemo(() => {
        return [...parts].sort((a, b) => (PART_TYPE_ORDER[a.type] ?? 99) - (PART_TYPE_ORDER[b.type] ?? 99));
    }, [parts]);

    // Group parts into sections
    const proximalParts = sorted.filter((p) => p.type === 'bifurcated_up' || p.type === 'cuff');
    const sidedParts = sorted.filter((p) => SIDED_PART_TYPES.has(p.type));
    const otherParts = sorted.filter((p) => !proximalParts.includes(p) && !sidedParts.includes(p));

    const hasSidedParts = sidedParts.length > 0;

    const bilateralParts = sidedParts.filter((p) => p.type === 'bifurcated_down');
    const ipsiParts = sidedParts.filter((p) => p.type === 'ipsi');
    const contraParts = sidedParts.filter((p) => p.type === 'contra');

    const proximalItems = useMemo(() => buildRenderableItems(proximalParts), [proximalParts]);
    const bilateralItems = useMemo(() => buildRenderableItems(bilateralParts), [bilateralParts]);
    const ipsiItems = useMemo(() => buildRenderableItems(ipsiParts), [ipsiParts]);
    const contraItems = useMemo(() => buildRenderableItems(contraParts), [contraParts]);
    const otherItems = useMemo(() => buildRenderableItems(otherParts), [otherParts]);

    const renderItem = (item: RenderableItem, sectionSide: 'left' | 'right' | 'none') => {
        if (item.kind === 'group') {
            const partType = item.parts[0].type;
            const side: Side =
                sectionSide === 'none' ? 'none' : resolvePartSide(partType, sectionSide, introductionSide);
            const groupDisabled = item.parts.some(
                (p) => p.unique === true && addedUniqueKeys.has(`${p.id}:${side}`)
            );
            const firstPart = item.parts[0];
            const key = `group-${item.groupName}-${sectionSide}`;
            return wrapCollapsible(
                firstPart,
                key,
                <ExclusivePartGroup
                    key={key}
                    parts={item.parts}
                    side={side}
                    introductionSide={introductionSide}
                    measurementLookup={measurementLookup}
                    onAdd={onAdd}
                    disabled={groupDisabled}
                />
            );
        }

        const side: Side =
            sectionSide === 'none' ? 'none' : resolvePartSide(item.part.type, sectionSide, introductionSide);
        const uniqueDisabled = item.part.unique === true && addedUniqueKeys.has(`${item.part.id}:${side}`);
        const key = `${item.part.id}-${sectionSide}`;
        return wrapCollapsible(
            item.part,
            key,
            <PartSelector
                key={key}
                part={item.part}
                side={side}
                introductionSide={introductionSide}
                measurementLookup={measurementLookup}
                onAdd={onAdd}
                disabled={uniqueDisabled}
            />
        );
    };

    const leftLabel = introductionSide === 'left' ? 'Left Side (Ipsi)' : 'Left Side (Contra)';
    const rightLabel = introductionSide === 'right' ? 'Right Side (Ipsi)' : 'Right Side (Contra)';

    const leftDirectional = introductionSide === 'left' ? ipsiItems : contraItems;
    const rightDirectional = introductionSide === 'left' ? contraItems : ipsiItems;

    return (
        <div className="flex flex-col">
            {proximalItems.map((item) => renderItem(item, 'none'))}

            {hasSidedParts && (
                <>
                    <SectionHeader label={leftLabel} />
                    {bilateralItems.map((item) => renderItem(item, 'left'))}
                    {leftDirectional.map((item) => renderItem(item, 'left'))}
                </>
            )}

            {hasSidedParts && (
                <>
                    <SectionHeader label={rightLabel} />
                    {bilateralItems.map((item) => renderItem(item, 'right'))}
                    {rightDirectional.map((item) => renderItem(item, 'right'))}
                </>
            )}

            {otherItems.length > 0 && (
                <>
                    {hasSidedParts && <SectionHeader label="Other" />}
                    {otherItems.map((item) => renderItem(item, 'none'))}
                </>
            )}
        </div>
    );
}
